import { Injectable, OnDestroy, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UploadRequestData } from '@app/components/upload-request-modal/upload-request-modal.component';
import { assignmentStartApiError } from '@app/pages/admin/assignment-start.util';
import { MapClickContext, RequestFormContext } from '../utils/map-click-context.models';
import { MenuPosition, canOpenMapRequestMenu, getEffectiveZoom, menuPositionForPointer, toMapClickContext } from '../utils/map-request-menu.util';
import {
  ProcessingRequestError,
  SatelliteProcessParameters,
  buildProcessingRequestParameters,
} from '../utils/processing-request-params.util';
import { MapClickFieldLookupService } from './map-click-field-lookup.service';
import { MapRequestAccessService } from './map-request-access.service';
import { SatelliteProcessingRequestService } from './satellite-processing-request.service';

/** What the flow needs from a map right-click. */
export interface MapRightClickInput {
  lat?: unknown;
  lng?: unknown;
  clientX?: number;
  clientY?: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * Page-scoped orchestration of "create a satellite-image processing request from the map":
 * permission and drawing-mode gate, context menu state, the bounded field lookup, the request form
 * state and the submit. The page component only wires template events to this class.
 *
 * Rules: a new right-click, the header button, closing or replacing the form, and destroy cancel a
 * pending lookup; map move/zoom/click only hide the menu. A finished submit whose form was closed or
 * replaced is ignored, so it can never close or write an error into a newer form.
 */
@Injectable()
export class MapRequestFlowService implements OnDestroy {
  private readonly access = inject(MapRequestAccessService);
  private readonly lookup = inject(MapClickFieldLookupService);
  private readonly requests = inject(SatelliteProcessingRequestService);
  private readonly messages = inject(MessageService);
  private readonly translate = inject(TranslateService);

  menuVisible = false;
  menuPosition: MenuPosition = { top: 0, left: 0 };
  menuContext: MapClickContext | null = null;

  formOpen = false;
  /** Null when the form was opened with the header button. */
  formContext: RequestFormContext | null = null;
  submitting = false;
  submitError = '';
  /** True while the field lookup of a chosen menu action runs (at most 1.5 s). */
  lookupPending = false;

  private permitted = false;
  private isDrawingActive: () => boolean = () => false;
  private lookupSub: Subscription | null = null;
  private submitSub: Subscription | null = null;
  private accessSub: Subscription | null = null;
  /** Bumped whenever the form is opened, replaced or closed; guards late submit results. */
  private formEpoch = 0;

  /** Starts watching the permission; `isDrawingActive` tells whether a drawing mode owns right-click. */
  connect(isDrawingActive: () => boolean): void {
    this.isDrawingActive = isDrawingActive;
    this.accessSub?.unsubscribe();
    this.accessSub = this.access.canCreateRequest().subscribe((allowed) => (this.permitted = allowed));
  }

  /** A new right-click replaces the previous context entirely and cancels any pending lookup. */
  onRightClick(
    event: MapRightClickInput,
    viewport: Viewport = { width: window.innerWidth, height: window.innerHeight },
    zoom: number = getEffectiveZoom(),
  ): void {
    this.cancelLookup();
    this.hideMenu();
    this.menuContext = null;
    if (!canOpenMapRequestMenu({ permitted: this.permitted, drawingActive: this.isDrawingActive() })) {
      return;
    }
    const context = toMapClickContext(event);
    if (!context) {
      return;
    }
    this.menuContext = context;
    this.menuPosition = menuPositionForPointer(event.clientX ?? 0, event.clientY ?? 0, viewport, zoom);
    this.menuVisible = true;
  }

  /** Hides the menu only; a lookup the user already started keeps running. */
  hideMenu(): void {
    this.menuVisible = false;
  }

  /**
   * Menu action: look the field up (failures and the 1.5 s limit mean "no field"), then open the form.
   * The form opens only from the lookup result, and only if nothing newer cancelled it.
   */
  chooseAction(context: MapClickContext): void {
    this.cancelLookup();
    this.hideMenu();
    this.lookupPending = true;
    this.lookupSub = this.lookup.findFieldAtPoint(context.lat, context.lng).subscribe((field) => {
      this.lookupSub = null;
      this.lookupPending = false;
      this.openForm(
        field
          ? { lat: context.lat, lng: context.lng, field, fieldOptions: [field] }
          : { lat: context.lat, lng: context.lng },
      );
    });
  }

  /** Header button: the form without a map context. */
  openFromHeader(): void {
    this.cancelLookup();
    this.openForm(null);
  }

  /** The form asked to open or close. Closing is refused while a submit is in flight. */
  onFormOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.formOpen = true;
      return;
    }
    if (this.submitting) {
      return;
    }
    this.closeForm();
  }

  /**
   * Form submit. Without a map context nothing is started (the header form keeps its old behaviour).
   * With one, starts `satellite.find-products` for the point or field.
   */
  submit(data: UploadRequestData): void {
    if (this.submitting || !data.context) {
      return;
    }
    if (!data.date) {
      this.submitError = this.translate.instant('MAP_REQUEST.SUBMIT_INCOMPLETE');
      return;
    }
    let parameters: SatelliteProcessParameters;
    try {
      parameters = buildProcessingRequestParameters({ satellite: data.satellite, date: data.date, context: data.context });
    } catch (error) {
      if (!(error instanceof ProcessingRequestError)) {
        throw error;
      }
      this.submitError = this.translate.instant(
        error.code === 'unknown-satellite' ? 'MAP_REQUEST.UNKNOWN_SATELLITE' : 'MAP_REQUEST.INVALID_COORDINATES',
      );
      return;
    }

    const epoch = this.formEpoch;
    this.submitting = true;
    this.submitError = '';
    this.submitSub = this.requests.startRequest(parameters).subscribe({
      next: () => {
        if (epoch !== this.formEpoch) {
          return;
        }
        this.submitting = false;
        this.closeForm();
        this.messages.add({ severity: 'success', summary: this.translate.instant('MAP_REQUEST.SUBMIT_SUCCESS') });
      },
      error: (error: unknown) => {
        if (epoch !== this.formEpoch) {
          return;
        }
        this.submitting = false;
        this.submitError = assignmentStartApiError(error, this.translate.instant('MAP_REQUEST.SUBMIT_ERROR'));
      },
    });
  }

  ngOnDestroy(): void {
    this.cancelLookup();
    this.dropSubmit();
    this.accessSub?.unsubscribe();
    this.accessSub = null;
  }

  private openForm(context: RequestFormContext | null): void {
    this.cancelLookup();
    this.dropSubmit();
    this.formEpoch++;
    this.formContext = context;
    this.submitError = '';
    this.formOpen = true;
  }

  private closeForm(): void {
    this.cancelLookup();
    this.dropSubmit();
    this.formEpoch++;
    this.formOpen = false;
    this.formContext = null;
    this.menuContext = null;
    this.submitError = '';
  }

  private dropSubmit(): void {
    this.submitSub?.unsubscribe();
    this.submitSub = null;
    this.submitting = false;
  }

  private cancelLookup(): void {
    this.lookupSub?.unsubscribe();
    this.lookupSub = null;
    this.lookupPending = false;
  }
}
